/**
 * Card view of the deals list — the alternative to the table.
 *
 * A card carries the same facts as a table row, so switching view loses
 * nothing: who it is, what became of them, what they pay, when it closed,
 * and who owns it.
 *
 * Opening a card opens the detail view, not the edit form. Reading a deal is
 * the common case and editing is the exception, which is what the table has
 * always done — the card sent people into a form they had not asked for, and
 * a form is a bad place to read from: every fact is a field.
 */
import { useApp } from '../store/AppContext.jsx';
import { parseDate, daysAgo, formatDate, periodLabel } from '../lib/dates.js';
import Icon from '../lib/icons.jsx';
import { EmptyState, Avatar, StagePill, OwnerPill, SegmentPill } from './Primitives.jsx';
import {
  isOpen, isStalled, companyName, lastTouch, clientOfDeal, dealValue
} from '../domain/crm.js';

export default function DealCards({ list, onRemove }) {
  const { S, crm, rows, fmtMoney, setSheet, convertDeal } = useApp();

  if (!list.length) {
    return (
      <EmptyState icon="card" tone="g-orange" title="No deals match">
        Clear the filters, or add a deal.
      </EmptyState>
    );
  }

  return (
    <div className="cards dgrid">
      {list.map(d => {
        const touch = lastTouch(crm, d.id);
        const close = parseDate(d.close);
        const stale = isStalled(S, crm, d);
        const linked = clientOfDeal(rows, d);
        const overdue = isOpen(d) && close && close < new Date();
        const needsConvert = d.stage === 'Won' && !linked;
        const v = dealValue(rows, d);
        const plans = String(d.plan || '').split(/[,;+]/).map(s => s.trim()).filter(Boolean);

        return (
          <article
            className={`dcard2${linked ? ' won' : ''}`}
            key={d.id}
            role="button"
            tabIndex={0}
            onClick={() => setSheet({ kind: 'dealView', id: d.id })}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSheet({ kind: 'dealView', id: d.id }); }
            }}
          >
            <header>
              <Avatar name={d.title} />
              <div className="dc2who">
                <b>{d.title}</b>
                <SegmentPill value={d.segment} />
                <small>{companyName(crm, d.cid) || 'No company'}</small>
              </div>
              <StagePill value={d.stage} />
            </header>

            {/* The one figure the card is really for, given the room a figure
                deserves - and saying Free or a dash rather than AED 0, which
                meant two different things and looked like one. */}
            <div className="dc2money">
              <span className="dc2amt">
                {v.free ? 'Free' : v.known ? `${fmtMoney(v.amount)}${periodLabel(v.cycle)}` : '—'}
              </span>
              <span className="dc2lab">
                {v.free ? 'on the free plan'
                  : v.known ? 'what they pay'
                    : linked ? 'no amount on the client record' : 'nothing forecast'}
              </span>
            </div>

            <dl className="dc2facts">
              <div>
                <dt>Closed</dt>
                <dd className={overdue ? 'bad' : ''}>{close ? formatDate(close, false, S.dateFmt) : '—'}</dd>
              </div>
              <div>
                <dt>Became</dt>
                <dd>{linked ? linked.name : <span className="dim">not yet a client</span>}</dd>
              </div>
            </dl>

            <div className="dc2chips">
              {linked ? <span className="dealchip cli"><Icon name="check" />Client</span> : null}
              {!d.origin ? <span className="dealchip man"><Icon name="edit" />Manual</span> : null}
              {plans.length ? (
                <span className="dealchip plan" title={`Plans discussed: ${plans.join(', ')}`}>
                  <Icon name="tag" />{plans[0]}{plans.length > 1 ? ` +${plans.length - 1}` : ''}
                </span>
              ) : null}
              {d.cycle ? <span className="dealchip">{d.cycle}</span> : null}
              {stale ? <span className="dealchip bad"><Icon name="alert" />Stale</span> : null}
            </div>

            <footer>
              <OwnerPill name={d.owner} />
              <span className="tsince">
                {touch ? `${daysAgo(touch)}d ago` : 'never touched'}
              </span>
              <div className="acts">
                {needsConvert ? (
                  <button
                    type="button"
                    className="won"
                    title="Move to clients"
                    onClick={e => { e.stopPropagation(); convertDeal(d.id); }}
                  >
                    <Icon name="check" />
                  </button>
                ) : null}
                <button
                  type="button"
                  className="vw"
                  title="View details"
                  onClick={e => { e.stopPropagation(); setSheet({ kind: 'dealView', id: d.id }); }}
                >
                  <Icon name="eye" />
                </button>
                <button
                  type="button"
                  title="Edit"
                  onClick={e => { e.stopPropagation(); setSheet({ kind: 'deal', id: d.id }); }}
                >
                  <Icon name="edit" />
                </button>
                <button type="button" className="rm" title="Delete" onClick={e => onRemove(e, d.id)}>
                  <Icon name="trash" />
                </button>
              </div>
            </footer>
          </article>
        );
      })}
    </div>
  );
}
